import { useTranslation } from "react-i18next";
import { BookingPageFormDialogContentsSectionForm } from "./types";
import { Checkbox, Label, MultiSelect, NumberInput } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { useCachedTags } from "@tellescope/tanstack-query";

export interface RestrictionsSectionProps {
  form: BookingPageFormDialogContentsSectionForm<
    | "collectReason"
    | "limitedToCareTeam"
    | "limitedByState"
    | "hoursBeforeBookingAllowed"
    | "limitedByTagsPortal"
    | "dontRestrictRescheduleToOriginalHost"
  >;
}

export const RestrictionsSection = ({ form }: RestrictionsSectionProps) => {
  const { t } = useTranslation("calendar");

  const { data: availableTags } = useCachedTags({
    model: "users",
  });

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="collectReason">{t("Reason For Appointment")}</Label>
        <Select {...form.getCustomFieldProps("collectReason")}>
          <Select.Trigger className="w-full" id="collectReason">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="Do Not Collect">{t("Do Not Collect")}</Select.Item>
            <Select.Item value="Optional">{t("Optional")}</Select.Item>
            <Select.Item value="Required">{t("Required")}</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <span className="flex gap-2">
        <Checkbox
          checked={form.values.limitedToCareTeam}
          onCheckedChange={(checked) => form.setFieldValue("limitedToCareTeam", !!checked)}
          id="limitedToCareTeam"
        />
        <Label htmlFor="limitedToCareTeam" className="flex flex-col gap-2 items-start">
          {t("Limit Portal Booking to Care Team")}
          <span className="font-light text-muted-foreground">{t("Only show care team members")}</span>
        </Label>
      </span>
      <span className="flex gap-2">
        <Checkbox
          checked={form.values.limitedByState}
          onCheckedChange={(checked) => form.setFieldValue("limitedByState", !!checked)}
          id="limitedByState"
        />
        <Label htmlFor="limitedByState" className="flex flex-col gap-2 items-start">
          {t("Restrict by Credentialed State")}
          <span className="font-light text-muted-foreground">{t("Match provider credentials to patient state")}</span>
        </Label>
      </span>
      <div className="space-y-2">
        <Label htmlFor="hoursBeforeBookingAllowed">{t("Hours in Advance")}</Label>
        <NumberInput
          value={form.values.hoursBeforeBookingAllowed}
          onChange={(num) => form.setFieldValue("hoursBeforeBookingAllowed", num ?? 0)}
          id="hoursBeforeBookingAllowed"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="limitedByTagsPortal">{t("Limit Portal Booking By Tags")}</Label>
        <MultiSelect
          className="h-auto py-0 w-full font-light"
          value={form.values.limitedByTagsPortal ?? []}
          onValueChange={(tags) => form.setFieldValue("limitedByTagsPortal", tags)}
          options={Array.from(new Set([...availableTags, ...(form.values.limitedByTagsPortal ?? [])])).map((tag) => ({
            label: tag,
            value: tag,
          }))}
          creator={{
            render: (inputValue) => <p>{t("Add '{{tag}}' tag", { tag: inputValue })}</p>,
            transform: (inputValue) => inputValue,
            onCreate: (val) =>
              form.setFieldValue(
                "limitedByTagsPortal",
                Array.from(new Set([...(form.values.limitedByTagsPortal ?? []), val.value]))
              ),
          }}
          placeholder={t("Add a tag...")}
        />
      </div>
      <span className="flex gap-2">
        <Checkbox
          checked={form.values.dontRestrictRescheduleToOriginalHost}
          onCheckedChange={(checked) => form.setFieldValue("dontRestrictRescheduleToOriginalHost", !!checked)}
          id="dontRestrictRescheduleToOriginalHost"
        />
        <Label htmlFor="dontRestrictRescheduleToOriginalHost" className="flex flex-col gap-2 items-start">
          {t("Don't Restrict Reschedule to Original Host")}
          <span className="font-light text-muted-foreground">{t("Allow rebooking with different provider")}</span>
        </Label>
      </span>
    </section>
  );
};
